import { i18n } from 'boot/i18n';
import { Timestamp } from 'firebase/firestore';
import { chain, get, isEmpty, isEqual, join, range, sortBy } from 'lodash';
import { useTime } from 'src/composables/time';
import {
  SCHEDULING_HOURS_TOTAL_EXCLUDED_STATUSES,
} from 'src/constants/scheduling';

/**
 * Merges time intervals that overlap or touch into continuous blocks
 * Sorting by start first means each interval only needs comparing with the last block:
 * it either extends that block or begins a new one after a gap.
 *
 * @param {[number, number][]} intervals - `[startMs, endMs]` pairs, in any order. Not mutated.
 * @returns {[number, number][]} Non-overlapping blocks, sorted by start.
 */
const mergeOverlappingIntervals = (intervals) => {
  const sortedIntervals = [...intervals].sort(([startA], [startB]) => startA - startB);

  return sortedIntervals.reduce((blocks, [intervalStart, intervalEnd]) => {
    const lastBlock = blocks[blocks.length - 1];

    if (lastBlock && intervalStart <= lastBlock[1]) {
      lastBlock[1] = Math.max(lastBlock[1], intervalEnd);
    } else {
      blocks.push([intervalStart, intervalEnd]);
    }

    return blocks;
  }, []);
};

// eslint-disable-next-line import/prefer-default-export
export function useScheduling() {
  const {
    getDate,
    getDay,
    adjustDate,
    addToDate,
    startOfDate,
    subtractFromDate,
    daysInMonth,
  } = useTime();

  const recurrenceFrequencyLabels = {
    day: 'scheduling.recurrence.frequency.day',
    week: 'scheduling.recurrence.frequency.week',
    month: 'scheduling.recurrence.frequency.month',
    year: 'scheduling.recurrence.frequency.year',
  };

  // @todo This should eventually be internationalized but we'll live with the English version for now
  const ordinalSuffixes = {
    1: 'st',
    2: 'nd',
    3: 'rd',
  };

  /**
   * Returns the ordinal number with the appropriate suffix.
   *
   * @param {number} val - The number to convert to ordinal.
   * @returns {string} The ordinal number with the appropriate suffix.
   */
  const getOrdinalNumber = (val) => {
    return val + (ordinalSuffixes[val % 10] || 'th');
  };

  /**
   * Returns the label for a given recurrence frequency and interval.
   *
   * @param {string} frequency - The recurrence frequency.
   * @param {number} interval - The recurrence interval.
   * @returns {string|null} The label for the recurrence frequency and interval, or null if the label is not found.
   */
  const getRecurrenceFrequencyLabel = (frequency, interval) => {
    const label = recurrenceFrequencyLabels[frequency];
    if (!label) {
      return null;
    }
    return i18n.tc(label, interval);
  };

  /**
   * Returns the label for the monthly recurrence type based on the frequency and recurrence pattern.
   *
   * @param {string} frequency - The frequency of the recurrence (day, weekday, lastWeekday).
   * @param {object} recurrencePattern - The recurrence pattern object.
   * @returns {string} The label for the monthly recurrence type.
   */
  const getRecurrenceMonthlyTypeLabel = (frequency, recurrencePattern) => {
    switch (frequency) {
      case 'day': {
        const ordinalDay = getOrdinalNumber(recurrencePattern.frequencyMonthDay);
        return i18n.t('scheduling.recurrence.frequencyMonthTypeDay', { ordinalDay });
      }
      case 'weekday': {
        const weekday = get(i18n.tm('common.weekdays'), recurrencePattern.frequencyMonthWeekday);
        const ordinalWeek = getOrdinalNumber(recurrencePattern.frequencyMonthWeek);

        return i18n.t('scheduling.recurrence.frequencyMonthTypeWeekday', { ordinalWeek, weekday });
      }
      case 'lastWeekday': {
        const weekday = get(i18n.tm('common.weekdays'), recurrencePattern.frequencyMonthWeekday);
        return i18n.t('scheduling.recurrence.frequencyMonthTypeLastWeekday', { weekday });
      }
      default:
        return '';
    }
  };

  const getRecurrenceLabel = (recurrencePattern) => {
    const frequencyLabel = getRecurrenceFrequencyLabel(
      recurrencePattern.frequency,
      recurrencePattern.interval,
    );
    let recurrenceText = `${i18n.t('scheduling.recurrence.labelEvery')
    } ${recurrencePattern.interval > 1 ? `${recurrencePattern.interval} ` : ''}${
      frequencyLabel}`;
    if (recurrencePattern.frequency === 'week') {
      let weekdayText = '';
      const sortedWeekdays = sortBy(recurrencePattern.frequencyWeekdays);
      if (isEqual(sortedWeekdays, [0, 6])) {
        weekdayText = i18n.t('common.textWeekends').toLowerCase();
      } else if (isEqual(sortedWeekdays, [0, 1, 2, 3, 4, 5, 6])) {
        weekdayText = i18n.t('common.textAllDays').toLowerCase();
      } else if (sortedWeekdays.length > 1
          && isEqual(sortedWeekdays, range(sortedWeekdays[0], sortedWeekdays[sortedWeekdays.length - 1] + 1))) {
        weekdayText = chain(i18n.tm('common.weekdays'))
          .pick([sortedWeekdays[0], sortedWeekdays[sortedWeekdays.length - 1]])
          .map((weekday) => weekday.slice(0, 3))
          .join(' - ')
          .value();
      } else {
        weekdayText = chain(i18n.tm('common.weekdays'))
          .pick(recurrencePattern.frequencyWeekdays)
          .map((weekday) => weekday.slice(0, 3))
          .join(', ')
          .value();
      }
      recurrenceText += ` ${i18n.t('scheduling.recurrence.labelOn').toLowerCase()
      } ${weekdayText}`;
    } else if (recurrencePattern.frequency === 'month') {
      const monthTypeLabel = getRecurrenceMonthlyTypeLabel(
        recurrencePattern.frequencyMonthType,
        recurrencePattern,
      );

      recurrenceText += ` ${i18n.t('scheduling.recurrence.labelOn').toLowerCase()} ${monthTypeLabel}`;
    }

    return recurrenceText;
  };

  /**
   * The weekdays a weekly recurrence actually falls on, read off its appointments in the user's
   * current timezone.
   *
   * `frequencyWeekdays` is stored as plain weekday numbers, so those numbers only mean something in the
   * timezone that was active when the recurrence was created. Change timezone in a way that shifts
   * the date, and they describe days the appointments are no longer on. The appointments themselves
   * are instants, so deriving from them is correct in any timezone and across a DST boundary.
   *
   * @param {Object[]} appointments - the recurrence's appointments; needs the whole series, or a
   *   weekday the loaded window happens to miss would be dropped from the rule
   * @return {number[]} sorted, Sunday = 0
   */
  const getRecurrenceWeekdays = (appointments) => {
    const weekdays = appointments
      .map(({ scheduledStartTimestamp }) => scheduledStartTimestamp)
      .filter((timestamp) => timestamp instanceof Timestamp)
      .map((timestamp) => getDay(timestamp.toDate()));

    return [...new Set(weekdays)].sort((a, b) => a - b);
  };

  /**
   * The recurrence pattern to describe and edit in the current timezone: the saved one, unless its
   * appointments disagree with it *and* the disagreement looks like a timezone shift.
   *
   * A shift moves every weekday to exactly one other, so the two sets are the same size. A different
   * size means:
   *
   * - the series came back partially - callers ask only for open appointments, and past occurrences
   *   are `completed` or `rendered`
   * - its occurrences shifted unevenly across a DST change, so no single weekday set describes it
   *
   * Rewriting the rule from either would drop or invent weekdays, and the dialog saves whatever it was shown,
   * so returning the saved object rather than a copy keeps compared patterns from seeing a change that did not happen.
   *
   * @param {?Object} recurrencePattern
   * @param {Object[]} appointments - the recurrence's appointments
   * @return {?Object}
   */
  const withShiftedWeekdays = (recurrencePattern, appointments) => {
    if (recurrencePattern?.frequency !== 'week') {
      return recurrencePattern;
    }

    const savedWeekdays = [...(recurrencePattern.frequencyWeekdays || [])].sort((a, b) => a - b);
    const derivedWeekdays = getRecurrenceWeekdays(appointments);

    const isShift = derivedWeekdays.length === savedWeekdays.length
      && derivedWeekdays.some((weekday, index) => weekday !== savedWeekdays[index]);

    return isShift
      ? { ...recurrencePattern, frequencyWeekdays: derivedWeekdays }
      : recurrencePattern;
  };

  const applyRecurrenceRule = (appointment, recurrenceRule) => {
    if (!(appointment.scheduledStartTimestamp instanceof Timestamp)) {
      return [];
    }

    const stripTime = (d) => {
      return startOfDate(d, 'day');
    };

    const isBeforeDate = (a, b) => {
      const dateA = stripTime(a);
      const dateB = stripTime(b);
      return dateA <= dateB;
    };

    const recurrences = [];

    const appointmentDate = appointment.scheduledStartTimestamp.toDate();
    const recurrenceEndDate = recurrenceRule.endDate instanceof Timestamp
      ? recurrenceRule.endDate.toDate()
      : null;
    const maxDate = addToDate(new Date(), { years: 10 });

    let currentDate = appointmentDate;
    let currentInterval = 1;

    while (
      (
        (recurrenceRule.endType === 'times' && currentInterval < recurrenceRule.endTimes)
      || (recurrenceRule.endType === 'date' && isBeforeDate(currentDate, recurrenceEndDate))
      )
      && (currentInterval <= 365)
      && (currentDate <= maxDate)
    ) {
      switch (recurrenceRule.frequency) {
        case 'day': {
          currentDate = addToDate(currentDate, { days: recurrenceRule.interval });
          if (recurrenceRule.endType !== 'date' || isBeforeDate(currentDate, recurrenceEndDate)) {
            recurrences.push(currentDate);
          }
          break;
        }
        case 'week': {
          const currentWeekday = getDay(currentDate);
          const nextWeekday = chain(recurrenceRule.frequencyWeekdays)
            .sortBy()
            .reject((weekday) => weekday <= currentWeekday)
            .head()
            .defaultTo(null)
            .value();
          if (nextWeekday === null) {
            const weekday = getDay(currentDate);
            if (weekday > 0) {
              currentDate = subtractFromDate(currentDate, { days: weekday });
            }
            currentDate = addToDate(currentDate, {
              days: 7 * recurrenceRule.interval + Math.min(...recurrenceRule.frequencyWeekdays),
            });
          } else {
            currentDate = addToDate(currentDate, { days: nextWeekday - currentWeekday });
          }
          if (recurrenceRule.endType !== 'date' || isBeforeDate(currentDate, recurrenceEndDate)) {
            recurrences.push(currentDate);
          }
          break;
        }
        case 'month': {
          if (recurrenceRule.frequencyMonthType === 'day') {
            const targetMonth = addToDate(
              adjustDate(currentDate, { day: 1 }),
              { month: recurrenceRule.interval },
            );

            // Luxon rolls an out-of-range day into the next month (Feb 31 -> Mar 3), which would
            // carry currentDate past targetMonth and cost a whole month on the next iteration.
            currentDate = recurrenceRule.frequencyMonthDay > daysInMonth(targetMonth)
              ? targetMonth
              : adjustDate(targetMonth, { day: recurrenceRule.frequencyMonthDay });

            // Skip the occurrence when the requested day does not exist in this month
            if (getDate(currentDate) === recurrenceRule.frequencyMonthDay
              && (recurrenceRule.endType !== 'date' || isBeforeDate(currentDate, recurrenceEndDate))) {
              recurrences.push(currentDate);
            }
          } else if (recurrenceRule.frequencyMonthType === 'weekday') {
            const firstDayOfMonth = addToDate(adjustDate(currentDate, { day: 1 }), { month: recurrenceRule.interval });

            const weekday = recurrenceRule.frequencyMonthWeekday;
            const firstDayWeekday = getDay(firstDayOfMonth);
            const weekdayDiff = ((weekday - firstDayWeekday + 7) % 7) + (7 * (recurrenceRule.frequencyMonthWeek - 1));

            currentDate = addToDate(
              firstDayOfMonth,
              { days: weekdayDiff },
            );
            const monthDays = daysInMonth(firstDayOfMonth);
            if (monthDays > weekdayDiff
              && (recurrenceRule.endType !== 'date' || isBeforeDate(currentDate, recurrenceEndDate))) {
              recurrences.push(currentDate);
            }
          } else if (recurrenceRule.frequencyMonthType === 'lastWeekday') {
            const lastDayOfMonth = subtractFromDate(
              addToDate(
                adjustDate(currentDate, { day: 1 }),
                { month: recurrenceRule.interval + 1 },
              ),
              { days: 1 },
            );
            const lastdayWeekday = getDay(lastDayOfMonth);
            if (lastdayWeekday === recurrenceRule.frequencyMonthWeekday) {
              currentDate = lastDayOfMonth;
            } else {
              currentDate = subtractFromDate(
                lastDayOfMonth,
                { days: (lastdayWeekday - recurrenceRule.frequencyMonthWeekday + 7) % 7 },
              );
            }
            if (recurrenceRule.endType !== 'date' || isBeforeDate(currentDate, recurrenceEndDate)) {
              recurrences.push(currentDate);
            }
          }
          break;
        }
        case 'year': {
          currentDate = addToDate(currentDate, { years: recurrenceRule.interval });
          if (recurrenceRule.endType !== 'date' || isBeforeDate(currentDate, recurrenceEndDate)) {
            recurrences.push(currentDate);
          }
          break;
        }
        default:
          break;
      }
      currentInterval += 1;
    }
    return recurrences;
  }; // applyRecurrenceRule()

  const getRecurrenceEndDate = (appointment, recurrencePattern) => {
    const recurrences = applyRecurrenceRule(appointment, recurrencePattern);
    if (recurrences.length > 0) {
      const value = recurrences[recurrences.length - 1];
      // Check if the last value is a valid Date
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value;
      }
      return null;
    }
    const appointmentStartTimestamp = appointment?.scheduledStartTimestamp || null;
    return appointmentStartTimestamp instanceof Timestamp
      ? appointmentStartTimestamp.toDate()
      : null;
  };

  const getTreatmentServiceCodeAndModifiers = (treatmentService) => {
    if (isEmpty(treatmentService)) {
      return '';
    }
    if (isEmpty(treatmentService.modifiers)) {
      return treatmentService.serviceCode;
    }
    return `${treatmentService.serviceCode} [${join(treatmentService.modifiers, ', ')}]`;
  }; // getTreatmentServiceCodeAndModifiers()

  const getOverlapSeconds = (appointment, start, end) => {
    const startDate = appointment.scheduledStartTimestamp?.toDate?.();
    if (!startDate) {
      return 0;
    }
    const endDate = new Date(startDate.getTime() + (appointment.duration || 0) * 1000);
    const overlapStart = startDate < start ? start : startDate;
    const overlapEnd = endDate > end ? end : endDate;
    return Math.max(0, (overlapEnd.getTime() - overlapStart.getTime()) / 1000);
  };

  // Only an explicit `false` is non-payable: integration appointments are created without `isPayable`.
  const isCountedAppointment = (appointment, { getStatus, excludeTypes = [], excludeNonPayable = false }) => (
    !excludeTypes.includes(appointment.type)
    && !(excludeNonPayable && appointment.isPayable === false)
    && !SCHEDULING_HOURS_TOTAL_EXCLUDED_STATUSES.includes(getStatus(appointment))
  );

  /**
   * Sums scheduled seconds per resource (e.g. staff member or client) over a date window, counting
   * each second once even when several of the resource's appointments cover it - e.g. a 9:00-13:00
   * session plus a 10:00-11:00 supervision is 4 hours, not 5. An appointment overlapping a window
   * boundary contributes only the portion of its duration that actually falls within
   * `[start, end)` - e.g. one starting 30 minutes before a week boundary and running for an hour
   * contributes 30 minutes to that week, and the remaining 30 to the next. Cancelled/deleted
   * appointments are excluded entirely, as is any `type` listed in `excludeTypes`. With
   * `excludeNonPayable`, appointments with Payable turned off are left out before the union, so a
   * break overlapping a session removes nothing from it.
   *
   * @param {Object[]} appointments - Appointments to sum, e.g. `appointmentsWithDrafts`. Must
   *   already include any appointment whose interval overlaps the window, even one starting
   *   before `start` - the padded appointment cache (`extendCachePeriod`) guarantees this for the
   *   scheduling page.
   * @param {Object} options
   * @param {Date} options.start - Window start (inclusive).
   * @param {Date} options.end - Window end (exclusive).
   * @param {(appointment: Object) => string} options.getStatus - Resolves the effective status
   *   (e.g. `getStatusBasedOnLinkedActivity`), since a linked session activity can override the
   *   status stored on the appointment.
   * @param {string} options.resourceKey - Appointment field identifying the resource to key the
   *   result by, e.g. `'user'` for staff hours, `'client'` for client hours.
   * @param {string[]} [options.excludeTypes] - Appointment `type` values to exclude entirely, e.g.
   *   `['unavailability']` for staff hours, `['unavailability', 'staffEvent']` for client hours.
   * @param {boolean} [options.excludeNonPayable] - Leave out appointments with `isPayable: false`,
   *   e.g. for staff hours. Client hours keep them: Payable is the staff member's pay, not therapy time.
   * @returns {Record<string, number>} Map of resource id -> seconds covered within the window.
   */
  const sumScheduledSecondsByResource = (appointments, {
    start, end, getStatus, resourceKey, excludeTypes = [], excludeNonPayable = false,
  }) => {
    const intervalsByResourceId = appointments.reduce((intervalsById, appointment) => {
      const resourceId = appointment[resourceKey];
      if (!resourceId || !isCountedAppointment(appointment, { getStatus, excludeTypes, excludeNonPayable })) {
        return intervalsById;
      }
      const startDate = appointment.scheduledStartTimestamp?.toDate?.();
      if (!startDate) {
        return intervalsById;
      }
      const endDate = new Date(startDate.getTime() + (appointment.duration || 0) * 1000);
      const overlapStart = startDate < start ? start : startDate;
      const overlapEnd = endDate > end ? end : endDate;
      const overlapSeconds = (overlapEnd.getTime() - overlapStart.getTime()) / 1000;
      if (overlapSeconds <= 0) {
        return intervalsById;
      }
      if (!intervalsById[resourceId]) {
        intervalsById[resourceId] = [];
      }
      intervalsById[resourceId].push([overlapStart.getTime(), overlapEnd.getTime()]);
      return intervalsById;
    }, {});

    return Object.fromEntries(Object.entries(intervalsByResourceId).map(([resourceId, intervals]) => {
      const mergedIntervals = mergeOverlappingIntervals(intervals);
      const coveredMs = mergedIntervals.reduce((total, [intervalStart, intervalEnd]) => (
        total + (intervalEnd - intervalStart)
      ), 0);

      return [resourceId, coveredMs / 1000];
    }));
  }; // sumScheduledSecondsByResource()

  /**
   * Sums scheduled seconds over any `[start, end)` window, with the same clipping and exclusions
   * as `sumScheduledSecondsByResource`.
   *
   * @param {Object[]} appointments
   * @param {Object} options
   * @param {Date} options.start - Window start (inclusive).
   * @param {Date} options.end - Window end (exclusive).
   * @param {(appointment: Object) => string} options.getStatus - Resolves the effective status
   *   (e.g. `getStatusBasedOnLinkedActivity`), since a linked session activity can override the
   *   status stored on the appointment.
   * @param {string[]} [options.excludeTypes] - Appointment `type` values to exclude entirely, e.g.
   *   `['unavailability', 'staffEvent']` for client hours.
   * @returns {number} Total seconds.
   */
  const sumScheduledSeconds = (appointments, {
    start, end, getStatus, excludeTypes = [],
  }) => {
    return appointments.reduce((seconds, appointment) => {
      if (!isCountedAppointment(appointment, { getStatus, excludeTypes })) {
        return seconds;
      }
      return seconds + getOverlapSeconds(appointment, start, end);
    }, 0);
  }; // sumScheduledSeconds()

  /**
   * Summarizes the series a recurrence pattern would create, counting the same occurrences
   * `applyRecurrenceRule` generates on save.
   *
   * `periodSeconds` is one full repeat period of the pattern (e.g. every 2 weeks), not a calendar
   * window, so a series starting mid-week or ending before a full week still reports a full week.
   *
   * @param {Object} appointment - Base appointment, with `scheduledStartTimestamp` and `duration`.
   * @param {Object} recurrencePattern
   * @returns {{ occurrences: number, totalSeconds: number, periodSeconds: number|null }}
   */
  const getRecurrenceSeriesSummary = (appointment, recurrencePattern) => {
    const series = [
      appointment,
      ...applyRecurrenceRule(appointment, recurrencePattern).map((occurrenceDate) => ({
        ...appointment,
        scheduledStartTimestamp: Timestamp.fromDate(occurrenceDate),
      })),
    ];
    const lastStart = series[series.length - 1].scheduledStartTimestamp.toMillis();
    const totalSeconds = sumScheduledSeconds(series, {
      start: series[0].scheduledStartTimestamp.toDate(),
      end: new Date(lastStart + (appointment.duration || 0) * 1000),
      getStatus: (occurrence) => occurrence.status, // Not created yet, so no linked activity can override the status.
    });

    const occurrencesPerPeriod = {
      day: 1,
      week: recurrencePattern.frequencyWeekdays?.length ?? 0,
      month: 1,
      year: 1,
    }[recurrencePattern.frequency];
    const periodSeconds = occurrencesPerPeriod === undefined
      ? null
      : occurrencesPerPeriod * (appointment.duration || 0);

    return { occurrences: series.length, totalSeconds, periodSeconds };
  }; // getRecurrenceSeriesSummary()

  /**
   * Formats scheduled seconds as localized "8 hr 15 min" / "8 hr" / "45 min" / "0 hr", via
   * `Intl.NumberFormat`'s short unit style - the same pattern used for a duration display in
   * `SessionNoteFieldPrompt.vue` (`getDisplayedDuration`). This handles pluralization and per-locale
   * wording for free, so no dictionary keys are needed for the unit strings.
   * Rounds to the nearest whole minute so a stray odd second cannot render "59 min" for a full hour.
   *
   * @param {number} seconds
   * @returns {string}
   */
  const formatScheduledHours = (seconds) => {
    const totalMinutes = Math.round((seconds || 0) / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const format = (value, unit) => new Intl.NumberFormat(i18n.locale, {
      style: 'unit', unit, unitDisplay: 'short',
    }).format(value);

    const parts = [];
    if (hours > 0 || minutes === 0) {
      parts.push(format(hours, 'hour'));
    }
    if (minutes > 0) {
      parts.push(format(minutes, 'minute'));
    }
    return parts.join(' ');
  }; // formatScheduledHours()

  return {
    getRecurrenceFrequencyLabel,
    getRecurrenceMonthlyTypeLabel,
    getRecurrenceLabel,
    getRecurrenceEndDate,
    getRecurrenceWeekdays,
    withShiftedWeekdays,
    applyRecurrenceRule,
    getTreatmentServiceCodeAndModifiers,
    sumScheduledSecondsByResource,
    sumScheduledSeconds,
    getRecurrenceSeriesSummary,
    formatScheduledHours,
  };
}
